import React, { useEffect, useState } from "react";
import SideBar from "../components/SideBar";
import { topCoinDistributionCardService } from "../services/topCoinDistributionCardService";
import { retailerService } from "../services/RetailerServices";
import TopCoinDistributionCardAssignForm from "../components/TopCoinDistributionCardAssignForm";
import TopCoinDistributionCardEditForm from "../components/TopCoinDistributionCardEditForm";
import TopCoinDistributionCardGenerateForm from "../components/TopCoinDistributionCardGenerateForm";
import TopCoinDistributionQrModal from "../components/TopCoinDistributionQrModal";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { MdOutlinePictureAsPdf } from "react-icons/md";

function ManageTopCoinDistributionCards() {
  const [assignments, setAssignments] = useState([]);
  const [retailers, setRetailers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [selectedCardForQr, setSelectedCardForQr] = useState(null);
  const navigate = useNavigate();

  const [expandedRow, setExpandedRow] = useState(null);
  const [batchRecords, setBatchRecords] = useState({});
  const [loadingRecords, setLoadingRecords] = useState(false);
  const [downloadingPdfId, setDownloadingPdfId] = useState(null);
  const [downloadingSingleId, setDownloadingSingleId] = useState(null);

  // Filters and pagination
  const [search, setSearch] = useState("");
  const [tierFilter, setTierFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [cardsRes, retailRes] = await Promise.all([
        topCoinDistributionCardService.getAllAssignments(),
        retailerService.getAllRetailers({ per_page: 1000 }),
      ]);
      if (cardsRes.status) {
        setAssignments(cardsRes.data);
      }
      if (retailRes.users) {
        setRetailers(retailRes.users);
      }
    } catch (error) {
      console.error("Failed to load data:", error);
      toast.error("Failed to load TopCoin assignments");
    } finally {
      setLoading(false);
    }
  };

  const filteredAssignments = assignments.filter((assign) => {
    const startSerialStr = assign.start_serial?.toString() || "";
    const endSerialStr = assign.end_serial?.toString() || "";
    const startCardStr = assign.start_card_no?.toString() || "";
    const endCardStr = assign.end_card_no?.toString() || "";
    const retailName = assign.retail_business?.first_name || "";
    const tier = assign.card_tier?.toLowerCase() || "";

    const matchesSearch =
      startSerialStr.toLowerCase().includes(search.toLowerCase()) ||
      endSerialStr.toLowerCase().includes(search.toLowerCase()) ||
      startCardStr.toLowerCase().includes(search.toLowerCase()) ||
      endCardStr.toLowerCase().includes(search.toLowerCase()) ||
      retailName.toLowerCase().includes(search.toLowerCase());

    const matchesTier = tierFilter === "all" || tier === tierFilter.toLowerCase();

    return matchesSearch && matchesTier;
  });

  const totalPages = Math.ceil(filteredAssignments.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedAssignments = filteredAssignments.slice(startIndex, startIndex + itemsPerPage);

  const handleDelete = async (id) => {
    if (window.confirm("Are you sure you want to unassign this batch of cards?")) {
      try {
        await topCoinDistributionCardService.deleteAssignment(id);
        toast.success("Assignment unassigned successfully.");
        fetchData();
      } catch (error) {
        toast.error("Failed to unassign batch");
      }
    }
  };

  const handleEdit = (assignment) => {
    setSelectedAssignment(assignment);
    setShowEditModal(true);
  };

  const handleDownloadPdf = async (assign) => {
    try {
      setDownloadingPdfId(assign.id);
      const retailName = assign.retail_business?.first_name || "Shop";
      const fileName = `TopCoin_QR_Cards_${retailName}_Assignment_${assign.id}.pdf`.replace(/\s+/g, "_");
      await topCoinDistributionCardService.downloadAssignmentQrPdf(assign.id, fileName);
      toast.success("QR Cards PDF downloaded successfully!");
    } catch (error) {
      console.error("Failed to download PDF:", error);
      toast.error("Failed to download QR code PDF");
    } finally {
      setDownloadingPdfId(null);
    }
  };

  const handleDownloadSinglePdf = async (record) => {
    try {
      setDownloadingSingleId(record.id);
      await topCoinDistributionCardService.downloadSingleCardQrPdf(record.id, record.card_number);
      toast.success(`PDF for card ${record.card_number} downloaded!`);
    } catch (error) {
      console.error("Failed to download single card PDF:", error);
      toast.error("Failed to download card PDF");
    } finally {
      setDownloadingSingleId(null);
    }
  };

  const handleRowClick = async (id) => {
    if (expandedRow === id) {
      setExpandedRow(null);
      return;
    }
    setExpandedRow(id);
    if (!batchRecords[id]) {
      setLoadingRecords(true);
      try {
        const res = await topCoinDistributionCardService.getBatchRecords(id);
        if (res.status) {
          setBatchRecords((prev) => ({ ...prev, [id]: res.data }));
        }
      } catch (error) {
        toast.error("Failed to load batch card records");
      } finally {
        setLoadingRecords(false);
      }
    }
  };

  return (
    <div className="manage-member-layout">
      <SideBar />
      <div className="main-content">
        <div className="header-bar" style={{ border: 0, boxShadow: "none" }}>
          <div className="header-top">
            <div>
              <h2 className="header-title">Manage TopCoin Distribution QR Cards</h2>
              <p style={{ margin: "4px 0 0", color: "#64748b", fontSize: "13px" }}>
                Generate QR cards, view bundle batches, and assign cards to partner retail shops.
              </p>
            </div>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                className="action-btn"
                onClick={() => setShowGenerateModal(true)}
                style={{ backgroundColor: "#10b981", display: "flex", alignItems: "center", gap: "6px" }}
              >
                + Create QR Cards
              </button>
              <button
                className="action-btn"
                onClick={() => navigate("/view-available-topcoin-distribution-cards")}
                style={{ backgroundColor: "#3b82f6", display: "flex", alignItems: "center", gap: "6px" }}
              >
                View QR Codes
              </button>
              <button
                className="action-btn"
                onClick={() => setShowAssignModal(true)}
                style={{ backgroundColor: "#1e3a8a", display: "flex", alignItems: "center", gap: "6px" }}
              >
                + Assign Cards
              </button>
            </div>
          </div>

          <div className="filters" style={{ display: "flex", gap: "15px", alignItems: "flex-end" }}>
            <div className="input-group" style={{ flex: 1 }}>
              <label>Search:</label>
              <input
                type="text"
                placeholder="Search by Retailer"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setCurrentPage(1);
                }}
                className="search-input"
                maxLength={50}
              />
            </div>

            {/* <div className="input-group" style={{ width: "200px" }}>
              <label>Filter by Tier:</label>
              <select
                value={tierFilter}
                onChange={(e) => {
                  setTierFilter(e.target.value);
                  setCurrentPage(1);
                }}
                className="search-input"
                style={{ padding: "8px 12px" }}
              >
                <option value="all">All Tiers</option>
                <option value="grey">Grey</option>
                <option value="brown">Brown</option>
                <option value="purple">Purple</option>
                <option value="red">Red</option>
                <option value="gold">Gold</option>
              </select>
            </div> */}
          </div> 

          {loading ? (
            <div style={{ textAlign: "center", padding: "30px", color: "#64748b" }}>Loading TopCoin assignments...</div>
          ) : (
            <div className="table-container">
              <table className="member-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>Retail Business</th>
                    <th>Card Tier</th>
                    <th>Value</th>
                    <th>Start Card No</th>
                    <th>End Card No</th>
                    <th>Start Serial</th>
                    <th>End Serial</th>
                    <th>Quantity</th>
                    <th>Status</th>
                    <th style={{ textAlign: "center", minWidth: "220px" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedAssignments.length > 0 ? (
                    paginatedAssignments.map((assign) => (
                      <React.Fragment key={assign.id}>
                        <tr
                          onClick={() => handleRowClick(assign.id)}
                          style={{ cursor: "pointer", transition: "background-color 0.2s" }}
                          onMouseOver={(e) => (e.currentTarget.style.backgroundColor = "#f8fafc")}
                          onMouseOut={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                        >
                          <td>{assign.retail_business_id}</td>
                          <td>
                            <strong>{assign.retail_business?.first_name} {assign.retail_business?.last_name}</strong>
                          </td>
                          <td>
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: "6px",
                                padding: "3px 8px",
                                borderRadius: "4px",
                                fontSize: "12px",
                                fontWeight: 700,
                                backgroundColor: `${assign.card_color}20`,
                                color: assign.card_color,
                                border: `1px solid ${assign.card_color}`,
                              }}
                            >
                              <span
                                style={{
                                  width: "8px",
                                  height: "8px",
                                  borderRadius: "50%",
                                  backgroundColor: assign.card_color,
                                }}
                              />
                              {assign.card_tier}
                            </span>
                          </td>
                          <td>
                            <strong style={{ color: "#0f172a" }}>
                              {assign.top_coin_value} TC
                            </strong>
                          </td>
                          <td><code style={{ fontSize: "12px" }}>{assign.start_card_no}</code></td>
                          <td><code style={{ fontSize: "12px" }}>{assign.end_card_no}</code></td>
                          <td><code style={{ fontSize: "12px", color: "#475569" }}>{assign.start_serial}</code></td>
                          <td><code style={{ fontSize: "12px", color: "#475569" }}>{assign.end_serial}</code></td>
                          <td>
                            <strong>{assign.total_assigned_quantity ?? assign.quantity}</strong>
                           {/* {(assign.redeemed_quantity > 0 || assign.inactive_quantity > 0) && (
                              <div style={{ fontSize: "11px", color: "#64748b", marginTop: "2px", lineHeight: "1.2" }}>
                                {assign.active_quantity || 0} active
                                {assign.redeemed_quantity > 0 && `, ${assign.redeemed_quantity} redeemed`}
                                {assign.inactive_quantity > 0 && `, ${assign.inactive_quantity} inactive`}
                              </div>
                            )}*/}
                          </td>
                          <td>
                            <span className={`status ${assign.status}`}>
                              {assign.status}
                            </span>
                          </td>
                          <td onClick={(e) => e.stopPropagation()}>
                            <div style={{ display: "flex", gap: "6px", justifyContent: "center", alignItems: "center" }}>
                              <button
                                onClick={() => handleDownloadPdf(assign)}
                                disabled={downloadingPdfId === assign.id}
                                title="Download all QR cards for this assignment as PDF"
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: "4px",
                                  padding: "4px 8px",
                                  fontSize: "12px",
                                  fontWeight: "600",
                                  cursor: downloadingPdfId === assign.id ? "wait" : "pointer",
                                  backgroundColor: "#0284c7",
                                  color: "#ffffff",
                                  border: "1px solid #0369a1",
                                  borderRadius: "4px",
                                  opacity: downloadingPdfId === assign.id ? 0.7 : 1,
                                  transition: "all 0.15s ease",
                                }}
                              >
                                <MdOutlinePictureAsPdf size={14} />
                                <span>{downloadingPdfId === assign.id ? "PDF..." : "QR PDF"}</span>
                              </button>
                              <button
                                className="btn-edit"
                                onClick={() => handleEdit(assign)}
                                style={{ padding: "4px 8px", fontSize: "12px", cursor: "pointer" }}
                              >
                                Edit
                              </button>
                              <button
                                onClick={() => handleDelete(assign.id)}
                                style={{
                                  padding: "4px 8px",
                                  fontSize: "12px",
                                  cursor: "pointer",
                                  backgroundColor: "#fee2e2",
                                  color: "#b91c1c",
                                  border: "1px solid #fca5a5",
                                  borderRadius: "4px",
                                }}
                              >
                                Unassign
                              </button>
                            </div>
                          </td>
                        </tr>
                        {expandedRow === assign.id && (
                          <tr>
                            <td colSpan="11" style={{ backgroundColor: "#f8fafc", padding: "16px", borderBottom: "1px solid #e2e8f0" }}>
                              {loadingRecords && !batchRecords[assign.id] ? (
                                <div style={{ textAlign: "center", padding: "10px", color: "#64748b" }}>Loading cards in this batch...</div>
                              ) : (
                                <div style={{ maxHeight: "320px", overflowY: "auto", border: "1px solid #e2e8f0", borderRadius: "8px", backgroundColor: "#fff" }}>
                                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "13px" }}>
                                    <thead style={{ backgroundColor: "#f1f5f9", position: "sticky", top: 0, zIndex: 1 }}>
                                      <tr>
                                        <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #cbd5e1" }}>Card Number</th>
                                        <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #cbd5e1" }}>Serial Number</th>
                                        <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #cbd5e1" }}>Tier & Value</th>
                                        <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #cbd5e1" }}>Status</th>
                                        <th style={{ padding: "8px 12px", textAlign: "center", borderBottom: "1px solid #cbd5e1" }}>QR Code</th>
                                        <th style={{ padding: "8px 12px", textAlign: "left", borderBottom: "1px solid #cbd5e1" }}>Assigned At</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {batchRecords[assign.id]?.length > 0 ? (
                                        batchRecords[assign.id].map((record) => (
                                          <tr key={record.id} style={{ borderBottom: "1px solid #f1f5f9" }}>
                                            <td style={{ padding: "8px 12px", fontFamily: "monospace", fontWeight: 600 }}>
                                              {record.card_number}
                                            </td>
                                            <td style={{ padding: "8px 12px", fontFamily: "monospace", color: "#475569" }}>
                                              {record.serial_number}
                                            </td>
                                            <td style={{ padding: "8px 12px" }}>
                                              <span
                                                style={{
                                                  padding: "2px 6px",
                                                  borderRadius: "4px",
                                                  fontSize: "11px",
                                                  fontWeight: 700,
                                                  backgroundColor: `${record.card_color}20`,
                                                  color: record.card_color,
                                                }}
                                              >
                                                {record.card_tier} ({record.top_coin_value} TC)
                                              </span>
                                            </td>
                                            <td style={{ padding: "8px 12px" }}>
                                              <span className={`status ${record.status}`} style={{ fontSize: "11px", padding: "2px 6px" }}>
                                                {record.status}
                                              </span>
                                            </td>
                                            <td style={{ padding: "8px 12px", textAlign: "center" }}>
                                              <div style={{ display: "inline-flex", gap: "6px", alignItems: "center", justifyContent: "center" }}>
                                                <button
                                                  type="button"
                                                  onClick={() => setSelectedCardForQr(record)}
                                                  style={{
                                                    padding: "3px 8px",
                                                    backgroundColor: "#3b82f6",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: "4px",
                                                    fontSize: "11px",
                                                    cursor: "pointer",
                                                    fontWeight: 600,
                                                  }}
                                                >
                                                  View QR
                                                </button>
                                                <button
                                                  type="button"
                                                  onClick={() => handleDownloadSinglePdf(record)}
                                                  disabled={downloadingSingleId === record.id}
                                                  title="Download Card QR PDF"
                                                  style={{
                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    gap: "3px",
                                                    padding: "3px 8px",
                                                    backgroundColor: "#0284c7",
                                                    color: "#fff",
                                                    border: "none",
                                                    borderRadius: "4px",
                                                    fontSize: "11px",
                                                    cursor: downloadingSingleId === record.id ? "wait" : "pointer",
                                                    fontWeight: 600,
                                                    opacity: downloadingSingleId === record.id ? 0.7 : 1,
                                                  }}
                                                >
                                                  <MdOutlinePictureAsPdf size={12} />
                                                  <span>{downloadingSingleId === record.id ? "..." : "PDF"}</span>
                                                </button>
                                              </div>
                                            </td>
                                            <td style={{ padding: "8px 12px", color: "#64748b" }}>
                                              {record.assigned_at ? new Date(record.assigned_at).toLocaleString() : "-"}
                                            </td>
                                          </tr>
                                        ))
                                      ) : (
                                        <tr>
                                          <td colSpan="6" style={{ padding: "12px", textAlign: "center", color: "#64748b" }}>
                                            No records found in this batch.
                                          </td>
                                        </tr>
                                      )}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" style={{ textAlign: "center", padding: "20px", color: "#64748b" }}>
                        No assignments found.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>

              <div className="pagination">
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                  disabled={currentPage === 1}
                >
                  &lt;
                </button>
                {Array.from({ length: totalPages }, (_, i) => (
                  <button
                    key={i}
                    className={`page-btn ${currentPage === i + 1 ? "active" : ""}`}
                    onClick={() => setCurrentPage(i + 1)}
                  >
                    {i + 1}
                  </button>
                ))}
                <button
                  className="page-btn"
                  onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                  disabled={currentPage === totalPages || totalPages === 0}
                >
                  &gt;
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {showAssignModal && (
        <TopCoinDistributionCardAssignForm
          retailers={retailers}
          onClose={() => setShowAssignModal(false)}
          onSuccess={() => {
            setShowAssignModal(false);
            fetchData();
          }}
        />
      )}

      {showGenerateModal && (
        <TopCoinDistributionCardGenerateForm
          onClose={() => setShowGenerateModal(false)}
          onSuccess={() => {
            setShowGenerateModal(false);
            fetchData();
          }}
        />
      )}

      {showEditModal && selectedAssignment && (
        <TopCoinDistributionCardEditForm
          assignment={selectedAssignment}
          onClose={() => {
            setShowEditModal(false);
            setSelectedAssignment(null);
          }}
          onSuccess={() => {
            setShowEditModal(false);
            setSelectedAssignment(null);
            fetchData();
          }}
        />
      )}

      {selectedCardForQr && (
        <TopCoinDistributionQrModal
          card={selectedCardForQr}
          onClose={() => setSelectedCardForQr(null)}
        />
      )}
    </div>
  );
}

export default ManageTopCoinDistributionCards;
